class LnCollectionTextAlignment extends HTMLElement {
  #mediaQuery = window.matchMedia("(min-width: 990px)");
  #resizeObserver = new ResizeObserver(() => this.#updateAlignment());
  #mutationObserver = new MutationObserver(() => this.#updateAlignment());
  #text;
  #grid;
  #collectionSection;

  connectedCallback() {
    this.#text = this.previousElementSibling;
    if (!this.#text?.classList.contains("ln-text-block")) return;

    this.#mediaQuery.addEventListener("change", this.#updateAlignment);
    window.addEventListener("resize", this.#updateAlignment);
    document.addEventListener("shopify:section:load", this.#updateAlignment);
    document.addEventListener("shopify:section:reorder", this.#updateAlignment);
    queueMicrotask(this.#updateAlignment);
  }

  disconnectedCallback() {
    this.#resizeObserver.disconnect();
    this.#mutationObserver.disconnect();
    this.#grid = undefined;
    this.#collectionSection = undefined;
    if (this.#text) this.#resetAlignment();
    this.#mediaQuery.removeEventListener("change", this.#updateAlignment);
    window.removeEventListener("resize", this.#updateAlignment);
    document.removeEventListener("shopify:section:load", this.#updateAlignment);
    document.removeEventListener(
      "shopify:section:reorder",
      this.#updateAlignment,
    );
  }

  #updateAlignment = () => {
    if (!this.isConnected || !this.#text) return;

    const collection = document.querySelector("collection-component");
    const collectionSection =
      collection?.closest(".shopify-section") ?? collection;
    if (collectionSection !== this.#collectionSection) {
      this.#collectionSection = collectionSection;
      this.#mutationObserver.disconnect();
      if (collectionSection) {
        this.#mutationObserver.observe(collectionSection, {
          childList: true,
          subtree: true,
        });
      }
    }

    const grid = collection?.querySelector(".product-grid");
    const parent = this.#text.parentElement;
    if (grid !== this.#grid) {
      this.#grid = grid;
      this.#resizeObserver.disconnect();
      if (grid) this.#resizeObserver.observe(grid);
      if (parent) this.#resizeObserver.observe(parent);
    }

    if (!this.#mediaQuery.matches || !grid || !parent) {
      this.#resetAlignment();
      return;
    }

    const gridBounds = grid.getBoundingClientRect();
    const parentBounds = parent.getBoundingClientRect();
    const parentStyle = getComputedStyle(parent);
    const offset =
      parentStyle.direction === "rtl"
        ? parentBounds.right -
          gridBounds.right -
          parseFloat(parentStyle.paddingRight) -
          parseFloat(parentStyle.borderRightWidth)
        : gridBounds.left -
          parentBounds.left -
          parseFloat(parentStyle.paddingLeft) -
          parseFloat(parentStyle.borderLeftWidth);

    if (gridBounds.width === 0 || parentBounds.width === 0) {
      this.#resetAlignment();
      return;
    }

    this.#text.style.setProperty("--ln-collection-grid-offset", `${offset}px`);
    this.#text.style.setProperty(
      "--ln-collection-grid-width",
      `${gridBounds.width}px`,
    );
    this.#text.setAttribute("data-ln-grid-aligned", "");
  };

  #resetAlignment() {
    this.#text.removeAttribute("data-ln-grid-aligned");
    this.#text.style.removeProperty("--ln-collection-grid-offset");
    this.#text.style.removeProperty("--ln-collection-grid-width");
  }
}

if (!customElements.get("ln-collection-text-alignment")) {
  customElements.define(
    "ln-collection-text-alignment",
    LnCollectionTextAlignment,
  );
}
